import { ChevronRight } from "lucide-react";
import { useState } from "react";
import DropdownMenu from "../../ui/DropdownMenu";

// Helper function to format billing frequency for display
function formatBillingFrequency(freq) {
  const map = {
    one_time: "one-time",
    per_hour: "hour",
    per_day: "day",
    per_week: "week",
    per_month: "month",
    per_quarter: "quarter",
    per_year: "year",
  };
  return map[freq] || freq;
}

export default function ItemList({
  items,
  onSelect,
  onArchive,
  onRestore,
  onDelete,
  onPriceUpdate, // 👈 NEW
}) {
  const [menuItem, setMenuItem] = useState(null);
  const [anchorRect, setAnchorRect] = useState(null);

  const [editingId, setEditingId] = useState(null);
  const [draftPrice, setDraftPrice] = useState("");

  function openMenu(e, item) {
    e.stopPropagation();
    setMenuItem(item);
    setAnchorRect(e.currentTarget.getBoundingClientRect());
  }

  function closeMenu() {
    setMenuItem(null);
    setAnchorRect(null);
  }

  function startEditPrice(e, item) {
    e.stopPropagation();
    setEditingId(item.id);
    setDraftPrice(item.price ?? "");
  }

  function commitPrice(item) {
    if (draftPrice !== item.price) {
      onPriceUpdate?.(item, Number(draftPrice));
    }
    setEditingId(null);
  }

  function cancelEdit() {
    setEditingId(null);
    setDraftPrice("");
  }

  return (
    <div className="bg-white border border-[var(--color-border)] rounded-lg">
      {/* Header */}
      <div className="px-5 py-3 border-b text-xs text-[var(--color-text-muted)]">
        Showing {items.length} items
      </div>

      {items.length === 0 && (
        <div className="px-5 py-16 text-center text-sm text-[var(--color-text-muted)]">
          No products or services found
        </div>
      )}

      {/* Rows */}
      <div className="divide-y divide-[var(--color-divider)]">
        {items.map((item) => {
          const isEditing = editingId === item.id;

          return (
            <div
              key={item.id}
              onClick={() => onSelect(item)}
              className={`
                group
                px-5 py-4
                flex items-center justify-between
                cursor-pointer
                rounded-md
                transition
                hover:bg-[var(--color-bg-muted)]
                hover:shadow-sm
                ${item.archived_at ? "opacity-60" : ""}
              `}
            >
              {/* LEFT */}
              <div className="space-y-1 min-w-0">
                <div className="font-medium text-sm truncate">{item.name}</div>

                <div className="flex items-center gap-2 text-xs text-[var(--color-text-muted)]">
                  {/* Type */}
                  <span>
                    {item.item_type === 'services' || item.type === 'service' 
                      ? 'Service' 
                      : 'Product'}
                  </span>

                  <span className="w-1 h-1 rounded-full bg-gray-300" />

                  {/* Tax */}
                  <span>
                    {(item.tax ?? item.tax_rate ?? item.gst_rate) 
                      ? `${item.tax ?? item.tax_rate ?? item.gst_rate}% GST` 
                      : "No tax"}
                  </span>

                  <span className="w-1 h-1 rounded-full bg-gray-300" />

                  {/* Status */}
                  {item.archived_at ? (
                    <span className="text-red-500 font-medium">Archived</span>
                  ) : (
                    <span className="text-emerald-600 font-medium">Active</span>
                  )}
                </div>
              </div>

              {/* RIGHT */}
              <div className="flex items-center gap-4">
                {/* PRICE */}
                <div
                  className="text-right"
                  onClick={(e) => startEditPrice(e, item)}
                >
                  {isEditing ? (
                    <input
                      type="number"
                      autoFocus
                      value={draftPrice}
                      onChange={(e) => setDraftPrice(e.target.value)}
                      onBlur={() => commitPrice(item)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") commitPrice(item);
                        if (e.key === "Escape") cancelEdit();
                      }}
                      className="
                        w-24
                        text-sm
                        px-2 py-1
                        border border-[var(--color-border)]
                        rounded-md
                        focus:outline-none
                        focus:ring-1 focus:ring-[var(--color-primary)]/40
                      "
                    />
                  ) : (
                    <>
                      <div className="text-sm font-medium">
                        ₹ {item.price ?? item.rate ?? 0}
                        {(item.item_type === 'services' || item.type === 'service') && item.billingFrequency && 
                          ` / ${formatBillingFrequency(item.billingFrequency)}`
                        }
                      </div>
                      <div className="text-[11px] text-[var(--color-text-muted)]">
                        {(item.item_type === 'services' || item.type === 'service') 
                          ? "Per service" 
                          : "Per unit"}
                      </div>
                    </>
                  )}
                </div>

                <ChevronRight className="text-gray-400" />

                <button
                  onClick={(e) => openMenu(e, item)}
                  className="
                    opacity-0
                    group-hover:opacity-100
                    p-1 rounded-md
                    hover:bg-gray-100
                  "
                >
                  ⋯
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Context Menu */}
      {menuItem && (
        <DropdownMenu anchorRect={anchorRect} onClose={closeMenu}>
          <button
            onClick={() => {
              closeMenu();
              onSelect(menuItem);
            }}
            className="w-full text-left px-3 py-2 hover:bg-gray-50"
          >
            Edit
          </button>

          {!menuItem.archived_at ? (
            <button
              onClick={() => {
                closeMenu();
                onArchive(menuItem);
              }}
              className="w-full text-left px-3 py-2 text-red-600 hover:bg-red-50"
            >
              Archive
            </button>
          ) : (
            <>
              <button
                onClick={() => {
                  closeMenu();
                  onRestore(menuItem);
                }}
                className="w-full text-left px-3 py-2 text-emerald-600 hover:bg-emerald-50"
              >
                Restore
              </button>

              <button
                onClick={() => {
                  closeMenu();
                  onDelete(menuItem);
                }}
                className="w-full text-left px-3 py-2 text-red-700 hover:bg-red-100"
              >
                Delete permanently
              </button>
            </>
          )}
        </DropdownMenu>
      )}
    </div>
  );
}
